import { useEffect, useEffectEvent, useRef } from 'react';
import { useStore } from 'react-redux';
import type { RootState } from '@/store';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { buscarEventosDaSimulacao, buscarSimulacaoAtual } from '../store/actions';
import { selectSincronizacaoDaSimulacao } from '../store/selectors';
const INTERVALO_RAPIDO_MS = 1_000;
const INTERVALO_LENTO_MS = 5_000;

/**
 * Mantém a conversa em dia com o core (W3, CA-25). Cada ciclo relê a simulação atual (é de lá que
 * vem o `processando` dos três pontos, CA-10) e os eventos depois do último `seq` recebido; o
 * slice descarta repetição. Com a Lexi processando ou um comando em voo, o ciclo é de 1 s; no
 * resto, de 5 s.
 *
 * O timer só é refeito quando muda a simulação ou o ritmo. O ciclo em si lê o estado mais recente
 * pelo `useEffectEvent`, sem reassinar a cada evento que chega. Leitura ainda em voo não empilha
 * outra: o pedido fica marcado e sai assim que ela volta, já com o estado novo (uma simulação que
 * trocou no meio do ciclo não espera os 5 s seguintes).
 *
 * O encerramento só desliga o timer depois de confirmar a última página. Uma falha transitória
 * ou um histórico paginado continua recuperável, inclusive ao reconectar ou voltar à aba.
 */
export function useSimulacaoPolling(): void {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const {
    simulacaoId,
    encerrada,
    leituraFinalConcluida,
    rapido
  } = useAppSelector(selectSincronizacaoDaSimulacao);
  const cicloEmVoo = useRef(false);
  const cicloPedidoDuranteOutro = useRef(false);
  const ativo = useRef(false);
  const intervalo = rapido ? INTERVALO_RAPIDO_MS : INTERVALO_LENTO_MS;
  const sincronizar = useEffectEvent(async () => {
    if (!ativo.current) return;
    if (cicloEmVoo.current) {
      cicloPedidoDuranteOutro.current = true;
      return;
    }
    cicloEmVoo.current = true;
    try {
      do {
        cicloPedidoDuranteOutro.current = false;
        // O reducer pode ter avançado antes do próximo render do React.
        const atual = selectSincronizacaoDaSimulacao(store.getState());
        if (!atual.simulacaoId || atual.encerrada && atual.leituraFinalConcluida) return;
        if (atual.carregandoEventos) {
          cicloPedidoDuranteOutro.current = true;
          return;
        }
        await Promise.all([dispatch(buscarSimulacaoAtual()), dispatch(buscarEventosDaSimulacao({
          simulacaoId: atual.simulacaoId,
          aposSeq: atual.ultimoSeq
        }))]);
      } while (cicloPedidoDuranteOutro.current && ativo.current);
    } finally {
      cicloEmVoo.current = false;
    }
  });
  useEffect(() => {
    if (!simulacaoId || encerrada && leituraFinalConcluida) return;
    ativo.current = true;
    const desinscrever = store.subscribe(() => {
      if (cicloPedidoDuranteOutro.current && !cicloEmVoo.current && !selectSincronizacaoDaSimulacao(store.getState()).carregandoEventos) void sincronizar();
    });
    void sincronizar();
    const sincronizarSeVisivel = () => {
      if (document.visibilityState !== 'hidden') void sincronizar();
    };
    const timer = setInterval(sincronizarSeVisivel, intervalo);
    document.addEventListener('visibilitychange', sincronizarSeVisivel);
    window.addEventListener('online', sincronizarSeVisivel);
    return () => {
      ativo.current = false;
      desinscrever();
      clearInterval(timer);
      document.removeEventListener('visibilitychange', sincronizarSeVisivel);
      window.removeEventListener('online', sincronizarSeVisivel);
    };
  }, [store, simulacaoId, intervalo, encerrada, leituraFinalConcluida]);
}